import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaEdit, FaTrash, FaPlus, FaTimes } from 'react-icons/fa';

const QuestionEditModal = ({ show, onHide, section, onSave }) => {
  const [questions, setQuestions] = useState([]);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (section) {
      setQuestions(section.questions || []);
    }
  }, [section]);

  const handleQuestionEdit = (question) => {
    setEditingQuestion(question);
  };

  const handleQuestionSave = async (questionData) => {
    const token = localStorage.getItem('adminToken');
    try {
      const url = questionData._id
        ? `${process.env.REACT_APP_API_BASE_URL}/api/questions/admin/section/${section._id}/question/${questionData._id}`
        : `${process.env.REACT_APP_API_BASE_URL}/api/questions/admin/section/${section._id}/question`;

      const method = questionData._id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(questionData)
      });

      if (!response.ok) throw new Error('Failed to save question');

      const result = await response.json();
      setQuestions(result.data.questions);
      setEditingQuestion(null);
      setError('');
      onSave();
    } catch (error) {
      setError(error.message);
    }
  };

  const handleQuestionDelete = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;

    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/questions/admin/section/${section._id}/question/${questionId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (!response.ok) throw new Error('Failed to delete question');

      const result = await response.json();
      setQuestions(result.data.questions);
      setError('');
      onSave();
    } catch (error) {
      setError(error.message);
    }
  };

  const handleAddQuestion = () => {
    setEditingQuestion({
      text: '',
      order: questions.length,
      isRequired: false,
      helpText: ''
    });
  };

  if (!section || !show) return null;

  return createPortal(
    <div className="modal-overlay" onClick={onHide}>
      <div className="modal-content !max-w-3xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-dark-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Edit Questions - {section.sectionTitle}</h2>
          <button onClick={onHide} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700"><FaTimes /></button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {error && <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">{error}</div>}
          <div className="mb-4">
            <button onClick={handleAddQuestion} className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white font-medium rounded-lg hover:bg-green-600"><FaPlus /> Add Question</button>
          </div>
          <div className="space-y-3">
            {questions.map((question, index) => (
              <div key={question._id || index} className="p-4 bg-gray-50 dark:bg-dark-700 rounded-xl border border-gray-200 dark:border-dark-600">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="text-gray-900 dark:text-white"><span className="font-semibold">Q{index + 1}:</span> {question.text}</p>
                    {question.helpText && <p className="text-sm text-gray-500 mt-1">Help: {question.helpText}</p>}
                    {question.isRequired && <span className="inline-block mt-2 px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-700 rounded">Required</span>}
                  </div>
                  <div className="flex gap-1 ml-4">
                    <button onClick={() => handleQuestionEdit(question)} className="p-2 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg"><FaEdit /></button>
                    <button onClick={() => handleQuestionDelete(question._id)} className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"><FaTrash /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {editingQuestion && <QuestionForm question={editingQuestion} onSave={handleQuestionSave} onCancel={() => setEditingQuestion(null)} />}
        </div>
      </div>
    </div>,
    document.body
  );
};

const QuestionForm = ({ question, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    text: question.text || '',
    helpText: question.helpText || '',
    isRequired: question.isRequired || false,
    order: question.order || 0
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ ...question, ...formData });
  };

  return (
    <div className="mt-4 p-4 bg-white dark:bg-dark-800 rounded-xl border-2 border-primary-200 dark:border-primary-800">
      <h6 className="font-semibold text-gray-900 dark:text-white mb-4">{question._id ? 'Edit Question' : 'Add New Question'}</h6>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Question Text</label>
          <textarea rows={3} value={formData.text} onChange={(e) => setFormData(prev => ({ ...prev, text: e.target.value }))} required className="w-full px-3 py-2 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Help Text (Optional)</label>
          <input type="text" value={formData.helpText} onChange={(e) => setFormData(prev => ({ ...prev, helpText: e.target.value }))} className="w-full px-3 py-2 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Order</label>
          <input type="number" value={formData.order} onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) }))} min="0" className="w-full px-3 py-2 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50" />
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={formData.isRequired} onChange={(e) => setFormData(prev => ({ ...prev, isRequired: e.target.checked }))} className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500" />
          <span className="text-sm text-gray-700 dark:text-gray-300">Required Question</span>
        </label>
        <div className="flex gap-2 pt-2">
          <button type="submit" className="px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700">{question._id ? 'Update Question' : 'Add Question'}</button>
          <button type="button" onClick={onCancel} className="px-4 py-2 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700">Cancel</button>
        </div>
      </form>
    </div>
  );
};

export default QuestionEditModal;